import React, { useState, useEffect } from "react";
import { Modal, Form, Button } from "react-bootstrap";
import Select from "react-select";
import { useDispatch, useSelector } from "react-redux";
import { deleteUser } from "../../store/actions/auth";

const DeleteUserModal = ({
  showHandler,
  hideHandler,
  modalTitle,
  className = "",
  modalTitleClass = "",
  center = true,
}) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state?.user);

  const [selectedReason, setSelectedReason] = useState(null);
  const [otherReasonText, setOtherReasonText] = useState("");
  const [errors, setErrors] = useState({});
  
  // Reset errors when selection changes
  useEffect(() => {
    if (selectedReason?.value !== 'other') {
      setOtherReasonText('');
    }
    setErrors({});
  }, [selectedReason]);
  
  // Validate form
  const validateForm = () => {
    const newErrors = {};
    if (!selectedReason) {
      newErrors.reason = 'Please select a reason for deletion';
    } else if (selectedReason.value === 'other') {
      if (!otherReasonText.trim()) {
        newErrors.otherReason = 'Please provide a reason';
      } else if (otherReasonText.length > 250) {
        newErrors.otherReason = 'Reason may not be greater than 250 characters';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };



  const reasonOptions = [
    { value: "failure_to_meet_requirements", label: "Failure to meet platform requirements" },
    { value: "incomplete_documentation", label: "Incomplete documentation" },
    { value: "policy_violation", label: "Policy violation" },
    { value: "other", label: "Other" },
  ];

  const handleConfirm = async (e) => {    
    if (!validateForm()) {
      return;
    }   
    try {
      const payload = {
        deleting_user_id:user?.userInfo?.id,
        reason: selectedReason.value,
        ...(selectedReason.value === "other" && { reason_text: otherReasonText })
      };
      await dispatch(deleteUser(payload));
      hideHandler();
    } catch (error) {
      setErrors({ global: error?.message });
    }
  };
  
  // Check if form is valid
  const isFormValid = selectedReason && (selectedReason.value !== 'other' || otherReasonText.trim());

  // Reset form and errors on modal close
  const handleClose = () => {
    setSelectedReason(null);
    setOtherReasonText("");
    setErrors({});
    hideHandler();
  };

  return (
    <Modal
    show={showHandler}
    onHide={handleClose}
    className={className || "cms-modal delete-modal"}
    centered={center}
  >
    <Modal.Header>
      <h3 className={`onboarding-title mb-0 ${modalTitleClass}`}>
        {modalTitle}
      </h3>
    </Modal.Header>
  
    <Modal.Body>
      <div>
        <p>You’re about to submit a request to delete your account and personal data. To confirm you're the account owner, we may contact you at your registered email or phone number. We can only proceed once you complete the verification steps outlined in our confirmation message.</p>
        <h5 className="delete-request-title">About account deletion requests:</h5>
        <ul className="list-unstyled ps-3 mb-4">
          <li className="mb-2">If you have any upcoming bookings or pending activities, you must cancel them before proceeding with your deletion request.</li>
          <li className="mb-2">Once your request is confirmed, your account will be scheduled for deletion after 90 days.</li>
          <li className="mb-2">During this 90-day period, you can restore your account at any time by simply logging in.</li>
          <li className="mb-2">After 90 days, your personal data will be anonymized and permanently deleted. Some data may be retained where legally required or permitted.</li>
          <li className="mb-2">After 90 days, If you choose to sign up again in the future with the same phone number, it will be treated as a new account with no access to past data or history.</li>
        </ul>
  
        {/* Reason Dropdown */}
        <Form.Group controlId="reason">
          <Form.Label className="fw-semibold">
          Why are you deleting your account? <span className="text-danger">*</span>
          </Form.Label>
          <Select
            options={reasonOptions}
            onChange={(selected) => setSelectedReason(selected)}
            value={selectedReason}
            placeholder="Select Reason"
            className={`react-select-container ${errors?.reason ? "is-invalid" : ""}`}
            classNamePrefix="select"
            isClearable
          />
          {errors?.reason && (
            <div className="invalid-feedback d-block">{errors?.reason}</div>
          )}
        </Form.Group>
  
        {/* Show Textarea if 'Other' is selected */}
        {selectedReason?.value === "other" && (
          <Form.Group controlId="otherReason" className="mt-3">
            <Form.Label className="fw-semibold">
              Please specify: <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={otherReasonText}
              maxLength={250}
              onChange={(e) => {
                setOtherReasonText(e.target.value);
                if (!e.target.value) setErrors({ otherReason: "" });
              }}
              placeholder="Write your reason here..."
              required
            />
            {errors?.otherReason && (
              <div className="invalid-feedback d-block">{errors?.otherReason}</div>
            )}
          </Form.Group>
        )}
  
        {/* Global Errors */}
        {errors && errors?.global && (
          <div className="text-danger mt-2">{errors?.global}</div>
        )}
  
        {/* Action Buttons */}
        <div className="d-flex justify-content-end gap-2 mt-4">
          <Button
            type="button"
            variant="outline-primary"
            onClick={handleClose}
            className="mr-md-2 mr-1 btn-sm"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={!isFormValid}
            className="btn-sm"
            onClick={handleConfirm}
          >
            Confirm
          </Button>
        </div>
      </div>
    </Modal.Body>
  </Modal>
  
  );
};

export default DeleteUserModal;
